import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Bell, UserPlus, BookOpen, Palette, Music, MessageSquare, Megaphone, Heart, Flame, Diamond, Bookmark } from 'lucide-react';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { useAuth } from '../App';

const API = '/api';

const Notifications = () => {
  const { isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const fetchUnreadCount = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUnreadCount(res.data.unread_count || 0);
    } catch {
      // silently fail
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    const onVisible = () => { if (!document.hidden) fetchUnreadCount(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', onVisible); };
  }, [isAuthenticated, fetchUnreadCount]);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/notifications?limit=15`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count ?? unreadCount);
    } catch {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (isOpen) => {
    setOpen(isOpen);
    if (isOpen) fetchNotifications();
  };

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((prev) => prev.map((n) => n.id === notificationId ? { ...n, read: true } : n));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch {
      // silently fail
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {
      // silently fail
    }
  };

  const getNotificationIcon = (type) => {
    const iconMap = {
      follow: <UserPlus className="w-4 h-4 text-orange-400" />,
      reaction: <Heart className="w-4 h-4 text-pink-400" />,
      comment: <MessageSquare className="w-4 h-4 text-blue-400" />,
      new_episode: <BookOpen className="w-4 h-4 text-green-400" />,
      new_artwork: <Palette className="w-4 h-4 text-purple-400" />,
      new_music: <Music className="w-4 h-4 text-cyan-400" />,
      system: <Megaphone className="w-4 h-4 text-yellow-400" />,
      artwork_saved: <Bookmark className="w-4 h-4 text-amber-400" />,
      series_saved: <Bookmark className="w-4 h-4 text-amber-400" />,
      episode_saved: <Bookmark className="w-4 h-4 text-amber-400" />,
      novel_saved: <Bookmark className="w-4 h-4 text-amber-400" />,
    };
    return iconMap[type] || <Bell className="w-4 h-4 text-zinc-500" />;
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    const diff = Date.now() - new Date(dateString).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(diff / 3600000);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(diff / 86400000);
    if (days < 7) return `${days}d ago`;
    return new Date(dateString).toLocaleDateString();
  };

  if (!isAuthenticated) return null;

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          className="relative flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-800 text-gray-300 transition-colors"
          aria-label="Notifications"
          title="Notifications"
          data-testid="notifications-dropdown-trigger"
        >
          <Bell className="w-4 h-4" />
          {unreadCount > 0 && (
            <span
              className="absolute -top-1 -right-1 bg-gradient-to-r from-orange-500 to-red-500 text-white text-[10px] rounded-full min-w-[16px] h-4 flex items-center justify-center px-1"
              data-testid="notifications-unread-badge"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="w-80 sm:w-96 bg-zinc-900 border-zinc-700 max-h-[480px] overflow-y-auto"
        data-testid="notifications-dropdown"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-700">
          <h3 className="font-bold text-white text-sm">Notifications</h3>
          {unreadCount > 0 && (
            <Button
              onClick={markAllAsRead}
              variant="ghost"
              size="sm"
              className="text-xs text-orange-400 hover:text-orange-300 h-auto py-1 px-2"
              data-testid="notifications-mark-all-read"
            >
              Mark all read
            </Button>
          )}
        </div>

        {/* Notification list */}
        {loading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500 mx-auto" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-8 text-center">
            <Bell className="w-10 h-10 text-zinc-600 mx-auto mb-2" />
            <p className="text-zinc-500 text-sm">No notifications yet</p>
          </div>
        ) : (
          notifications.map((n) => (
            <DropdownMenuItem
              key={n.id}
              className={`px-4 py-3 cursor-pointer focus:bg-zinc-800 ${!n.read ? 'bg-zinc-800/50' : ''}`}
              onClick={() => {
                if (!n.read) markAsRead(n.id);
                setOpen(false);
              }}
              asChild
            >
              <Link to={n.link || '#'} className="block">
                <div className="flex items-start gap-3 w-full">
                  {/* Actor avatar or icon */}
                  {n.actor_avatar ? (
                    <Avatar className="h-8 w-8 flex-shrink-0">
                      <AvatarImage src={n.actor_avatar} alt={n.actor_name} />
                      <AvatarFallback className="bg-zinc-700 text-xs">{(n.actor_name || '?')[0]}</AvatarFallback>
                    </Avatar>
                  ) : (
                    <div className="flex-shrink-0 mt-0.5 p-1.5 rounded-full bg-zinc-800">
                      {getNotificationIcon(n.type)}
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <p className={`text-sm leading-snug ${!n.read ? 'font-semibold text-white' : 'text-zinc-300'}`}>
                      {n.message || n.title}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      {getNotificationIcon(n.type)}
                      <span className="text-[10px] text-zinc-500">{formatTimeAgo(n.updated_at || n.created_at)}</span>
                    </div>
                  </div>

                  {!n.read && (
                    <div className="flex-shrink-0 mt-1.5">
                      <div className="w-2 h-2 bg-orange-500 rounded-full" />
                    </div>
                  )}
                </div>
              </Link>
            </DropdownMenuItem>
          ))
        )}

        <DropdownMenuSeparator className="bg-zinc-700" />
        <DropdownMenuItem asChild>
          <Link
            to="/notifications"
            className="block py-2.5 text-center text-sm text-orange-400 hover:text-orange-300"
            onClick={() => setOpen(false)}
            data-testid="notifications-view-all"
          >
            View all notifications
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default Notifications;
